import React from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

/**
 * LsConfirmDialog
 *
 * Props:
 * open          boolean
 * onClose       () => void
 * onConfirm     () => void | Promise<any>
 * title         string                        (default: "Are you sure?")
 * confirmLabel  string                        (default: "Confirm")
 * cancelLabel   string                        (default: "Cancel")
 * severity      "error"|"warning"|"info"|"question"  (default: "warning")
 * loading       boolean
 * maxWidth      "xs"|"sm"|"md"               (default: "xs")
 * children      ReactNode
 */

// All colors derived from the legal palette in theme.ts
const SEVERITY_CONFIG = {
    error: {
        Icon: ErrorOutlineRoundedIcon,
        iconColor: "#9B2226",   // error.main
        iconBg: "#FDECEA",   // error tint
        borderTop: "#9B2226",
        buttonColor: "error",
    },
    warning: {
        Icon: WarningAmberRoundedIcon,
        iconColor: "#B45309",   // warning.main
        iconBg: "#FEF3C7",   // warning tint
        borderTop: "#B45309",
        buttonColor: "warning",
    },
    info: {
        Icon: InfoOutlinedIcon,
        iconColor: "#1B3A6B",   // primary.main
        iconBg: "#E8EEF8",   // primary tint
        borderTop: "#1B3A6B",
        buttonColor: "primary",
    },
    question: {
        Icon: HelpOutlineRoundedIcon,
        iconColor: "#4A2C8A",   // status.legal purple
        iconBg: "#EDE8F5",
        borderTop: "#4A2C8A",
        buttonColor: "primary",
    },
};

export default function LsConfirmDialog({
                                            open,
                                            onClose,
                                            onConfirm,
                                            title = "Are you sure?",
                                            confirmLabel = "Confirm",
                                            cancelLabel = "Cancel",
                                            severity = "warning",
                                            loading: externalLoading = false,
                                            maxWidth = "xs",
                                            children,
                                        }) {
    const [internalLoading, setInternalLoading] = React.useState(false);
    const isLoading = externalLoading || internalLoading;

    const {Icon, iconColor, iconBg, borderTop, buttonColor} =
    SEVERITY_CONFIG[severity] ?? SEVERITY_CONFIG.warning;

    const handleConfirm = async () => {
        if (!onConfirm) return;
        const result = onConfirm();
        if (result instanceof Promise) {
            setInternalLoading(true);
            try {
                await result;
            } finally {
                setInternalLoading(false);
            }
        }
    };

    const handleClose = () => {
        if (isLoading) return;
        onClose?.();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth={maxWidth}
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                    overflow: "hidden",
                    // Severity accent bar along the top
                    borderTop: `3px solid ${borderTop}`,
                },
            }}
        >
            {/* ── Title ── */}
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: 2.5,
                    pt: 2,
                    pb: 1.5,
                }}
            >
                {/* Icon badge */}
                <Box
                    sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "6px",
                        bgcolor: iconBg,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                    }}
                >
                    <Icon sx={{fontSize: 18, color: iconColor}}/>
                </Box>

                <Typography
                    component="span"
                    sx={{fontSize: "0.9375rem", fontWeight: 600, color: "text.primary", lineHeight: 1.3}}
                >
                    {title}
                </Typography>
            </DialogTitle>

            <Divider/>

            {/* ── Body ── */}
            <DialogContent sx={{px: 2.5, py: 2}}>
                {typeof children === "string" ? (
                    <Typography variant="body2" color="text.secondary" sx={{lineHeight: 1.7}}>
                        {children}
                    </Typography>
                ) : (
                    <Box sx={{color: "text.secondary", fontSize: "0.875rem", lineHeight: 1.7}}>
                        {children}
                    </Box>
                )}
            </DialogContent>

            <Divider/>

            {/* ── Actions ── */}
            <DialogActions sx={{px: 2.5, py: 1.5, gap: 1, bgcolor: "background.default"}}>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={handleClose}
                    disabled={isLoading}
                    sx={{borderRadius: 1, minWidth: 80}}
                >
                    {cancelLabel}
                </Button>

                <Button
                    variant="contained"
                    size="small"
                    color={buttonColor}
                    onClick={handleConfirm}
                    disabled={isLoading}
                    disableElevation
                    startIcon={
                        isLoading
                            ? <CircularProgress size={13} color="inherit"/>
                            : undefined
                    }
                    sx={{borderRadius: 1, minWidth: 90}}
                >
                    {isLoading ? "Please wait…" : confirmLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
}